import { CURRENT_CYCLE_TITLE } from '@/components/DatePicker';
import { ShipmentPushStatus, ShipmentStatus } from '@/constants/shipment';
import { capitalizeFirstLetter } from '@/helpers';
import { NoOrderIcon } from '@/icons';
import { getShipmentStatusInfo } from '@/pages/Shipments/components/ShipmentTable/config';
import { useGetDashboardShipmentQuery } from '@/redux/api/api.caller';
import { datePickerSelector, titleDatePickerSelector } from '@/redux/features/dashboard.slice';
import { Badge, BlockStack, Card, Divider, InlineGrid, SkeletonBodyText, Text } from '@shopify/polaris';
import { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { StyledRecentActivities } from './styled';

const NoActivitiesData = () => (
  <div className="no-activities-data">
    <NoOrderIcon />
    <BlockStack gap="200">
      <Text variant="headingSm" as="h6">
        No orders found
      </Text>
      <Text variant="bodySm" as="p">
        Try to re-sync orders
      </Text>
    </BlockStack>
  </div>
);

const RecentActivities = () => {
  const { start, end } = useSelector(datePickerSelector);
  const titleDatePicker = useSelector(titleDatePickerSelector);
  const isCurrentCycle = titleDatePicker === CURRENT_CYCLE_TITLE;
  const shipmentParams = isCurrentCycle
    ? { inCycle: true }
    : { fromDate: start.toISOString(), toDate: end.toISOString() };
  const { data, isFetching } = useGetDashboardShipmentQuery(shipmentParams);

  const renderContent = () => {
    if (isFetching) {
      return (
        <div className="content-loading">
          <SkeletonBodyText lines={10} />
        </div>
      );
    }
    if (data && data.data.length === 0) {
      return <NoActivitiesData />;
    }
    return (
      <BlockStack gap="200">
        <InlineGrid columns={{ xs: 2, sm: 2, md: 2, lg: 2, xl: 2 }} gap="400">
          <Text variant="bodyLg" as="p">
            Order
          </Text>
          <Text variant="bodyLg" as="p">
            Status
          </Text>
        </InlineGrid>
        <div className="activity-list">
          {data &&
            data.data.map(({ name, trackingSummary, pushStatus }, index) => {
              const info = getShipmentStatusInfo(
                pushStatus === ShipmentPushStatus.OVER_QUOTA
                  ? ShipmentStatus.OverQuota
                  : pushStatus === ShipmentPushStatus.NOT_DETECT
                  ? ShipmentStatus.CourierNotDetected
                  : (trackingSummary as ShipmentStatus),
              );
              return (
                <Fragment key={index}>
                  <Divider />
                  <InlineGrid columns={{ xs: 2, sm: 2, md: 2, lg: 2, xl: 2 }} gap="400">
                    <p>{name}</p>
                    <span>
                      <Badge progress={info?.progress} tone={info?.tone}>
                        {capitalizeFirstLetter(
                          pushStatus === ShipmentPushStatus.OVER_QUOTA
                            ? ShipmentStatus.OverQuota.split('_').join(' ')
                            : pushStatus === ShipmentPushStatus.NOT_DETECT
                            ? ShipmentStatus.CourierNotDetected.split('_').join(' ')
                            : trackingSummary === ShipmentStatus.NotFound
                            ? 'No info'
                            : trackingSummary.split('_').join(' '),
                        )}
                      </Badge>
                    </span>
                  </InlineGrid>
                </Fragment>
              );
            })}
        </div>
      </BlockStack>
    );
  };

  return (
    <StyledRecentActivities>
      <Card>
        <BlockStack gap="400">
          <Text variant="headingMd" as="h2">
            Recent shipments
          </Text>
          {renderContent()}
        </BlockStack>
      </Card>
    </StyledRecentActivities>
  );
};

export default RecentActivities;
